export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type EndpointRule = {
	pattern: RegExp;
	methods: Set<HttpMethod>;
};

type TokenChangeListener = (available: boolean) => void;

import type { AxiosRequestConfig } from "axios";

/**
 * Singleton store for Altcha captcha payloads and a policy declaring
 * which API endpoints require a captcha. This avoids timing/cookie reliance
 * and ensures single-use payloads are consumed exactly once.
 */
export class CaptchaTokenService {
	private static instance: CaptchaTokenService;

	private currentToken: string | null = null;
	private rules: EndpointRule[] = [];
	private listeners: Set<TokenChangeListener> = new Set();

	private constructor() {
		this.registerEndpoints([/^auth\/(login)$/], new Set(["POST"]));
	}

	public static getInstance(): CaptchaTokenService {
		if (!CaptchaTokenService.instance) {
			CaptchaTokenService.instance = new CaptchaTokenService();
		}
		return CaptchaTokenService.instance;
	}

	public isTokenAvailable(): boolean {
		return !!(this.currentToken && this.currentToken.length > 0);
	}

	public subscribe(listener: TokenChangeListener): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners(): void {
		const available = this.isTokenAvailable();
		for (const listener of this.listeners) {
			try {
				listener(available);
			} catch (_err) {
				// ignore listener errors
			}
		}
	}

	public setToken(token: string): void {
		this.currentToken = token;
		this.notifyListeners();
	}

	public clearToken(): void {
		this.currentToken = null;
		this.notifyListeners();
	}

	public registerEndpoints(
		patterns: RegExp[],
		methods?: Set<HttpMethod>,
	): void {
		const methodSet = methods ?? new Set<HttpMethod>(["POST"]);
		for (const pattern of patterns) {
			this.rules.push({ pattern, methods: new Set(methodSet) });
		}
	}

	public setPolicy(
		rules: Array<{ pattern: RegExp; methods?: Set<HttpMethod> }>,
	): void {
		this.rules = rules.map((r) => ({
			pattern: r.pattern,
			methods: r.methods ? new Set(r.methods) : new Set<HttpMethod>(["POST"]),
		}));
	}

	public requiresCaptcha(
		pathname: string | undefined,
		method: string | undefined,
	): boolean {
		if (!pathname) return false;
		const normalizedPath = pathname.replace(/^\//, "");
		const normalizedMethod = (method ?? "GET").toUpperCase() as HttpMethod;
		for (const rule of this.rules) {
			if (
				rule.methods.has(normalizedMethod) &&
				rule.pattern.test(normalizedPath)
			) {
				return true;
			}
		}
		return false;
	}

	public consumeFor(
		pathname: string | undefined,
		method: string | undefined,
	): string | null {
		if (!this.requiresCaptcha(pathname, method)) return null;
		if (!this.currentToken || this.currentToken.length === 0) return null;
		const t = this.currentToken;
		this.currentToken = null;
		this.notifyListeners();
		return t;
	}

	public attachToAxiosConfig(
		config: AxiosRequestConfig,
		baseURL: string | URL | undefined,
	): void {
		try {
			const base = baseURL ? baseURL.toString() : "";
			let relativePath = (config.url ?? "").replace(/^\//, "");
			try {
				const baseUrl = new URL(base || window.location.origin);
				const resolved = new URL(config.url ?? "", baseUrl);
				relativePath = resolved.pathname
					.replace(baseUrl.pathname, "")
					.replace(/^\//, "");
			} catch (_) {
				// keep best-effort relativePath from above
			}

			const method = (config.method ?? "get").toUpperCase();
			const token = this.consumeFor(relativePath, method);
			if (!token) return;

			config.headers = config.headers ?? {};
			(config.headers as Record<string, string>)["x-altcha-payload"] = token;
		} catch (_) {
			// intentionally swallow errors
		}
	}
}

export default CaptchaTokenService;
